import { CButton, CCol, CFormGroup, CInput, CRow } from "@coreui/react";
import React, { useCallback, useState } from "react";
import { FieldErrors } from "../form/FieldErrors";
import Errors, { errorsAreSame, getFieldErrors } from "../../models/errors";
import { emptyValueOnUndefined } from "../../utils/fields";
import AccountingEntry from "../../models/accounting-entries";
import SingleAccountingAccountSelect from "../accounting-accounts/SingleAccountingAccountSelect";
import AccountingAccount from "../../models/accounting-accounts";
import CurrencyField from "../currencies/CurrencyField";
import { PYG } from "../../currency/available-currencies";

interface AccountingEntryFormItemProps {
  value: AccountingEntry;
  initialEntry?: AccountingEntry;
  initialAccount?: AccountingAccount;
  disabled?: boolean;
  onDelete: (_: AccountingEntry) => void;
  onChange: (_: AccountingEntry) => void;
  errors: Errors;
}

const AccountingEntryFormItem: React.FC<AccountingEntryFormItemProps> = ({
  value,
  initialAccount,
  disabled,
  onDelete,
  onChange,
  errors,
}) => {
  const [editingItem, setEditingItem] = useState<AccountingEntry>(value);
  const [account, setAccount] = useState<AccountingAccount | null>(
    initialAccount !== undefined ? initialAccount : null
  );

  const onDeleteClick = useCallback(() => {
    onDelete(editingItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem]);

  const onAccountChange = useCallback(
    (newAccount: AccountingAccount | null) => {
      setAccount(newAccount);
      const newItem: AccountingEntry = {
        ...editingItem,
        accountName: newAccount?.name,
        accountCode: newAccount?.codeIdentifier,
        accountId: newAccount?.id,
      };
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onDebitAmountChange = useCallback(
    (newAmount: number | undefined) => {
      const newItem: AccountingEntry = {
        ...editingItem,
        debitAmount: newAmount,
      };
      setEditingItem(newItem);
      onChange(newItem);
    },
    [editingItem, onChange]
  );

  const onCreditAmountChange = useCallback(
    (newAmount: number | undefined) => {
      const newItem: AccountingEntry = {
        ...editingItem,
        creditAmount: newAmount,
      };
      setEditingItem(newItem);
      onChange(newItem);
    },
    [editingItem, onChange]
  );

  const onSubjectChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem: AccountingEntry = {
        ...editingItem,
        subject: e.target.value,
      };
      setEditingItem(newItem);
      onChange(newItem);
    },
    [editingItem, onChange]
  );

  return (
    <div className="section border rounded mb-2 p-2">
      <CFormGroup>
        <CRow>
          <CCol md={2} className={"mt-2"}>
            Cuenta Contable:
          </CCol>
          <CCol md={4}>
            <SingleAccountingAccountSelect
              value={account}
              onChange={onAccountChange}
              disabled={disabled}
            ></SingleAccountingAccountSelect>
            <FieldErrors
              errors={getFieldErrors("accountId", errors) as string[]}
            ></FieldErrors>
          </CCol>
          <CCol md={2} className={"mt-2"}>
            Concepto:
          </CCol>
          <CCol md={4}>
            <CInput
              type="text"
              value={emptyValueOnUndefined(editingItem.subject)}
              onChange={onSubjectChange}
              placeholder="Concepto"
            ></CInput>
            <FieldErrors
              errors={getFieldErrors("subject", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
        <CRow className={"mt-2"}>
          <CCol md={2} className={"mt-2"}>
            Debe:
          </CCol>
          <CCol md={4}>
            <CurrencyField
              currency={PYG}
              value={editingItem.debitAmount}
              onChange={onDebitAmountChange}
            ></CurrencyField>
            <FieldErrors
              errors={getFieldErrors("creditAmount", errors) as string[]}
            ></FieldErrors>
          </CCol>
          <CCol md={2} className={"mt-2"}>
            Haber:
          </CCol>
          <CCol md={4}>
            <CurrencyField
              currency={PYG}
              value={editingItem.creditAmount}
              onChange={onCreditAmountChange}
            ></CurrencyField>
            <FieldErrors
              errors={getFieldErrors("creditAmount", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow>
          <CCol>
            <CButton
              className="btn btn-danger float-right"
              onClick={onDeleteClick}
              disabled={disabled}
            >
              <i className="fa fa-trash"></i>
            </CButton>
          </CCol>
        </CRow>
      </CFormGroup>
    </div>
  );
};

const propsAreEqual = (
  prevItemProps: AccountingEntryFormItemProps,
  nextItemProps: AccountingEntryFormItemProps
): boolean => {
  const areEqual =
    prevItemProps.value.id === nextItemProps.value.id &&
    prevItemProps.value.accountId === nextItemProps.value.accountId &&
    prevItemProps.value.creditAmount === nextItemProps.value.creditAmount &&
    prevItemProps.value.debitAmount === nextItemProps.value.debitAmount &&
    prevItemProps.value.subject === nextItemProps.value.subject &&
    errorsAreSame(prevItemProps.errors, nextItemProps.errors);

  return areEqual;
};

export default React.memo(AccountingEntryFormItem, propsAreEqual);
